import PersonOutline from "~icons/eva/person-outline"
import HomeOutline from "~icons/eva/home-outline"
import CreditCardOutline from "~icons/eva/credit-card-outline"
import ShieldOutline from "~icons/eva/shield-outline"
import StarOutline from "~icons/eva/star-outline"
import styles from "./styles.module.scss"
import { DataGrid, TDataGridItem } from "./../../organisms/BookingModal/components/DataGrid"
import { SelectItem } from "./../../organisms/BookingModal/components/SelectItem"
import { observer } from "mobx-react-lite"
import { useStores } from "../../../models/helpers/useStores"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo } from "react"
import {
  CardBrandNiceTypeMap,
  CruiseLineLoyaltyMemberID,
  CruisePassengerAgeGroup,
  ICruiseLine,
  IProfile,
  ITripRoomGuest,
  SaveProfileInput,
  TripUserPaymentMethod,
} from "@cruisen/contract"
import { convertMonthDayYearToDateString } from "@cruisen/react-shared"
import { SkeletonTheme } from "react-loading-skeleton"
import { colors } from "@theme/colors"
import CloseModalButton from "../../buttons/CloseModalButton"
import {
  getTripUsersPaymentMethods,
  revealCreditCard,
} from "../../../services/firebase-functions/billing"
import { makeAutoObservable } from "mobx"
import {
  generateProfileGridData,
  prepareTripRoomGuestProfile,
} from "../../organisms/BookingModal/utils"
import {
  deleteCruiseLineLoyaltyMember,
  revealPassportNumber,
  saveProfile,
  upsertCruiseLineLoyaltyMember,
} from "../../../services/firebase-functions/profile"
import { getCruiseLines } from "../../../services/firebase-functions/dictionaries"
import { AddLoyaltyNumberModal } from "../AddLoyaltyNumberModal"
import { usePaymentData } from "../../../hooks/usePaymentData"
import * as yup from "yup"
import { _t } from "@cruisen/i18n"
import { isPhoneValid } from "../../../utils/validation"
import { nameRegex } from "../../molecules/Room/modals/utils"
import { ConfirmationModal } from "../ConfirmationModal"

const getOnlyDigits = (value: string) => value.replace(/\D/g, "")

const getPersonalInfoSchema = (advisorPhoneNumber: string) =>
  yup.object().shape({
    title: yup.string().optional(),
    firstName: yup
      .string()
      .required("-")
      .matches(nameRegex, _t("validation.useOnlyEnglishLetters")),
    lastName: yup.string().required("-").matches(nameRegex, _t("validation.useOnlyEnglishLetters")),
    ageGroup: yup.string().required("-"),
    dob: yup.string().optional(),
    nationality: yup.string().optional(),
    phone: yup
      .string()
      .test("phone", "-", (value) => {
        if (!value) return true
        return isPhoneValid(value, true)
      })
      .test("phone", "You cannot use your own phone number", (value) => {
        if (!value) return true
        return getOnlyDigits(value) !== getOnlyDigits(advisorPhoneNumber)
      }),
    email: yup.string().optional(),
  })

const passportSchema = yup.object().shape({
  passportNumber: yup
    .string()
    .test(
      "passportNumber",
      _t("validation.passportNumberMinLength"),
      (value) => !value || value.length >= 5,
    ),
  passportIssueDate: yup.string(),
  passportExpireDate: yup.string(),
  passportIssuingCountry: yup.string(),
})

type ActiveModal = "addLoyaltyNumber" | "deleteLoyaltyNumber" | null

class GuestDetailsModalStore {
  selectedPaymentMethod: TripUserPaymentMethod | null
  gridData: TDataGridItem[]
  activeModal: ActiveModal
  selectedCruiseLineLoyaltyMemberId: CruiseLineLoyaltyMemberID | null
  selectedCruiseLine: ICruiseLine | null
  profile: IProfile | null
  unmaskedPassportNumber: string | null

  constructor(defaultProfile?: IProfile) {
    this.profile = defaultProfile || null
    this.selectedPaymentMethod = null
    this.gridData = []
    this.activeModal = null
    this.selectedCruiseLineLoyaltyMemberId = null
    this.selectedCruiseLine = null
    this.unmaskedPassportNumber = null

    makeAutoObservable(this)
  }

  setProfile(profile: IProfile) {
    this.profile = profile
  }

  setSelectedPaymentMethod(paymentMethod: TripUserPaymentMethod) {
    this.selectedPaymentMethod = paymentMethod
  }

  setGridData(gridData: TDataGridItem[]) {
    this.gridData = gridData
  }

  setActiveModal(activeModal: ActiveModal) {
    this.activeModal = activeModal
  }

  setSelectedCruiseLineLoyaltyMemberId(cruiseLineLoyaltyMemberId: CruiseLineLoyaltyMemberID) {
    this.selectedCruiseLineLoyaltyMemberId = cruiseLineLoyaltyMemberId
  }

  setSelectedCruiseLine(selectedCruiseLine: ICruiseLine) {
    this.selectedCruiseLine = selectedCruiseLine
  }

  setUnmaskedPassportNumber(unmaskedPassportNumber: string) {
    this.unmaskedPassportNumber = unmaskedPassportNumber
  }
}

export const GuestDetailsModal = observer(
  ({
    closeModal,
    guest,
    profile,
    roomId,
  }: {
    closeModal: () => void
    guest: ITripRoomGuest
    profile?: IProfile
    roomId: string
  }) => {
    const { activeTrip, loggedInUser } = useStores()
    const store = useMemo(() => new GuestDetailsModalStore(profile), [])

    const queryClient = useQueryClient()

    const { mutate: mutateDeleteCruiseLineLoyaltyMember, isPending: isDeleting } = useMutation({
      mutationFn: () =>
        deleteCruiseLineLoyaltyMember({
          cruiseLineLoyaltyMemberId: store.selectedCruiseLineLoyaltyMemberId,
          tripId: activeTrip.id,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["allProfiles", activeTrip.id] })
        store.setActiveModal(null)
        store.setSelectedCruiseLine(null)
        store.setSelectedCruiseLineLoyaltyMemberId(null)
      },
    })

    const { data: cruiseLines, isLoading: isCruiseLinesLoading } = useQuery({
      queryKey: ["cruiseLines"],
      queryFn: () => getCruiseLines(),
    })

    const { data: paymentMethods, isLoading: isPaymentMethodsLoading } = useQuery({
      queryKey: ["paymentMethods", activeTrip.id],
      queryFn: () => getTripUsersPaymentMethods({ tripId: activeTrip.id }),
      staleTime: 0,
    })

    const filteredPaymentMethods =
      paymentMethods?.filter((paymentMethod) => paymentMethod.userId === guest.userId) || []

    useEffect(() => {
      if (filteredPaymentMethods.length > 0) {
        store.setSelectedPaymentMethod(filteredPaymentMethods[0])
      }
    }, [paymentMethods])

    const { mutate: revealSelectedPaymentMethod, isPending: isPaymentMethodRevealing } =
      useMutation({
        mutationFn: async () => {
          if (!store.selectedPaymentMethod?.tokenizedCreditCard?.id) return

          const creditCard = await revealCreditCard({
            creditCardId: store.selectedPaymentMethod.tokenizedCreditCard.id,
          })

          if (
            creditCard &&
            store.selectedPaymentMethod.tokenizedCreditCard.tokenId === creditCard.tokenId
          ) {
            store.setSelectedPaymentMethod({
              ...store.selectedPaymentMethod,
              creditCard,
            })
          }
        },
      })

    const { paymentData } = usePaymentData({
      isPaymentMethodsLoading,
      selectedPaymentMethod: store.selectedPaymentMethod,
      isPaymentMethodRevealing,
    })

    const { mutate: revealPassport, isPending: isPassportRevealing } = useMutation({
      mutationFn: async () => {
        const passport = await revealPassportNumber({
          profileId: store.profile?.id,
          tripId: activeTrip.id,
        })
        if (
          passport?.result?.passportNumber &&
          store.profile?.passportTokenId === passport.result.passportTokenId
        ) {
          store.setUnmaskedPassportNumber(passport.result.passportNumber)
        }
      },
    })

    useEffect(() => {
      // Handles updating loyalty numbers
      if (profile) {
        store.setProfile(profile)
      }
    }, [profile])

    const tripRoomGuestProfile = prepareTripRoomGuestProfile(guest, store.profile)
    const gridData = generateProfileGridData({
      profile: tripRoomGuestProfile,
      unmaskedPassportNumber: store.unmaskedPassportNumber,
      isPassportRevealing,
    })

    const filteredCruiseLines =
      store.profile?.cruiseLineLoyaltyMembers?.length > 0
        ? cruiseLines?.filter(
            (cruiseLine) =>
              !store.profile?.cruiseLineLoyaltyMembers.some(
                (member) => member.cruiseLineId === cruiseLine.id,
              ),
          ) || []
        : cruiseLines

    const handleEditProfile = async (newProfile: SaveProfileInput["profile"]) => {
      const savedProfile = await saveProfile({
        profile: newProfile,
        tripId: activeTrip.id,
        tripRoomGuestData: {
          roomId: roomId,
          guestId: guest.id,
        },
      })

      if (savedProfile) {
        store.setUnmaskedPassportNumber(null)
        store.setProfile(savedProfile)
      }
    }

    const personalInfoSchema = getPersonalInfoSchema(loggedInUser?.phoneNumber)
    const hasPassportNumber = store.profile?.passportNumber

    return (
      <>
        <SkeletonTheme
          baseColor={colors.backgroundAdditional}
          highlightColor={colors.backgroundQuaternary}
        >
          <div className={styles.container}>
            <div className={styles.header}>
              <div>
                <h2>Guest details</h2>
                <p>
                  {guest.firstName} {guest.lastName}
                </p>
              </div>
              <CloseModalButton onClick={closeModal} />
            </div>
            <div className={styles.scrollContainer} data-testid="room-booking-modal">
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>General</h3>
                <DataGrid
                  icon={<PersonOutline className={styles.sectionIcon} />}
                  title="Personal info"
                  data={gridData.personalInfo}
                  defaultFormValues={gridData.personalInfo.reduce(
                    (acc, item) => {
                      acc[item.formName] = item.formValue || item.value
                      return acc
                    },
                    {} as Record<string, string>,
                  )}
                  resolver={personalInfoSchema}
                  onEdit={async (formValues) => {
                    const formattedDob = convertMonthDayYearToDateString(formValues.dob)

                    const newProfile: SaveProfileInput["profile"] = {
                      id: store.profile?.id || undefined,
                      ownerUserId: tripRoomGuestProfile.ownerUserId || undefined,
                      isPrimaryProfile: tripRoomGuestProfile.isPrimaryProfile,
                      firstName: formValues.firstName,
                      lastName: formValues.lastName,
                      title: formValues.title,
                      dob: formattedDob,
                      nationality: formValues.nationality,
                      phone: formValues.phone,
                      ageGroup: formValues.ageGroup as CruisePassengerAgeGroup,
                    }

                    await handleEditProfile(newProfile)
                  }}
                />
                <DataGrid
                  icon={<ShieldOutline className={styles.sectionIcon} />}
                  title="Passport details"
                  data={gridData.passportDetails}
                  defaultFormValues={gridData.passportDetails.reduce(
                    (acc, item) => {
                      acc[item.formName] = item.formValue || item.value
                      return acc
                    },
                    {} as Record<string, string>,
                  )}
                  onDataReveal={hasPassportNumber ? revealPassport : undefined}
                  resolver={passportSchema}
                  onEdit={async (formValues) => {
                    const formattedExpireDate = convertMonthDayYearToDateString(
                      formValues.passportExpireDate,
                    )

                    const formattedIssueDate = convertMonthDayYearToDateString(
                      formValues.passportIssueDate,
                    )

                    const newProfile: SaveProfileInput["profile"] = {
                      id: store.profile?.id || undefined,
                      ownerUserId: tripRoomGuestProfile.ownerUserId || undefined,
                      isPrimaryProfile: tripRoomGuestProfile.isPrimaryProfile,
                      passportNumber: formValues.passportNumber,
                      passportTokenId: tripRoomGuestProfile.passportTokenId,
                      passportExpireDate: formattedExpireDate,
                      passportIssueDate: formattedIssueDate,
                      passportIssuingCountry: formValues.passportIssuingCountry,
                      // First, last name and age group are required
                      firstName: tripRoomGuestProfile.firstName,
                      lastName: tripRoomGuestProfile.lastName,
                      ageGroup: tripRoomGuestProfile.ageGroup as CruisePassengerAgeGroup,
                    }

                    await handleEditProfile(newProfile)
                  }}
                />
                <DataGrid
                  icon={<StarOutline className={styles.sectionIcon} />}
                  title="Loyalty numbers"
                  data={gridData.cruiseLineLoyaltyMembers}
                  defaultFormValues={gridData.cruiseLineLoyaltyMembers.reduce(
                    (acc, item) => {
                      acc[item.formName] = item.formValue
                      return acc
                    },
                    {} as Record<string, string>,
                  )}
                  openDeleteLoyaltyNumberModal={(formName) => {
                    const cruiseLineId = Number(formName.split("+")[1])
                    const cruiseLineLoyaltyMember = profile?.cruiseLineLoyaltyMembers.find(
                      (member) => member.cruiseLineId === cruiseLineId,
                    )
                    const cruiseLine = cruiseLines.find(
                      (cruiseLine) => cruiseLine.id === cruiseLineId,
                    )
                    store.setSelectedCruiseLineLoyaltyMemberId(cruiseLineLoyaltyMember?.id)
                    store.setSelectedCruiseLine(cruiseLine)
                    store.setActiveModal("deleteLoyaltyNumber")
                  }}
                  openAddLoyaltyNumberModal={() => {
                    store.setActiveModal("addLoyaltyNumber")
                  }}
                  onEdit={async (formValues) => {
                    for (const [key, value] of Object.entries(formValues)) {
                      const cruiseLineId = Number(key.split("+")[1])
                      const cruiseLineLoyaltyMember = profile?.cruiseLineLoyaltyMembers.find(
                        (member) => member.cruiseLineId === cruiseLineId,
                      )
                      await upsertCruiseLineLoyaltyMember({
                        cruiseLineLoyaltyMember: {
                          loyaltyCode: value,
                          profileId: profile?.id,
                          cruiseLineId: cruiseLineId,
                          id: cruiseLineLoyaltyMember?.id,
                        },
                        tripId: activeTrip.id,
                      })
                    }
                  }}
                  cruiseLineLoyalty={true}
                />
                <DataGrid
                  icon={<HomeOutline className={styles.sectionIcon} />}
                  title="Residence"
                  data={gridData.residence}
                  defaultFormValues={gridData.residence.reduce(
                    (acc, item) => {
                      acc[item.formName] = item.formValue || item.value
                      return acc
                    },
                    {} as Record<string, string>,
                  )}
                  onEdit={async (formValues) => {
                    const newProfile: SaveProfileInput["profile"] = {
                      id: store.profile?.id || undefined,
                      ownerUserId: tripRoomGuestProfile.ownerUserId || undefined,
                      isPrimaryProfile: tripRoomGuestProfile.isPrimaryProfile,
                      address: formValues.address,
                      city: formValues.city,
                      country: formValues.country,
                      county: formValues.county,
                      postcode: formValues.postcode,
                      // First name, last name and age group are required
                      firstName: tripRoomGuestProfile.firstName,
                      lastName: tripRoomGuestProfile.lastName,
                      ageGroup: tripRoomGuestProfile.ageGroup as CruisePassengerAgeGroup,
                    }

                    await handleEditProfile(newProfile)
                  }}
                />
              </div>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Payment methods</h3>
                {!isPaymentMethodsLoading && filteredPaymentMethods.length === 0 ? (
                  <div className={styles.emptyStateContainer}>
                    <p className={styles.emptyStateTitle}>No payment methods added</p>
                    <p className={styles.emptyStateText}>
                      This guest does not have any payment methods added to their profile yet.
                    </p>
                  </div>
                ) : (
                  <div className={styles.paymentMethodsSection}>
                    <div className={styles.itemsList}>
                      {!isPaymentMethodsLoading ? (
                        filteredPaymentMethods.map((item) => {
                          const displayName = item.paymentMethodDetails.displayName
                          const label = item.tokenizedCreditCard
                            ? `${CardBrandNiceTypeMap.get(
                                item.tokenizedCreditCard.brandId,
                              )} ${displayName}`
                            : `${item.paymentMethodDetails.type} ${displayName}`

                          return (
                            <SelectItem
                              key={item.id}
                              label={label}
                              isActive={item.id === store.selectedPaymentMethod?.id}
                              onClick={() => store.setSelectedPaymentMethod(item)}
                              error={""}
                            />
                          )
                        })
                      ) : (
                        <SelectItem label={"••••"} isActive={true} />
                      )}
                    </div>
                    <DataGrid
                      icon={<CreditCardOutline className={styles.sectionIcon} />}
                      title="Payment info"
                      data={paymentData}
                      onDataReveal={revealSelectedPaymentMethod}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </SkeletonTheme>
        {store.activeModal === "deleteLoyaltyNumber" && !isCruiseLinesLoading && (
          <ConfirmationModal
            title="Remove loyalty number"
            description={`Are you sure you want to remove loyalty number for <b>${store.selectedCruiseLine?.name}</b> cruise line?`}
            onConfirm={mutateDeleteCruiseLineLoyaltyMember}
            isLoading={isDeleting}
            confirmButtonText={isDeleting ? "Removing..." : "Remove"}
            testId="delete-loyalty-number-modal"
            closeModal={() => {
              store.setActiveModal(null)
              store.setSelectedCruiseLine(null)
              store.setSelectedCruiseLineLoyaltyMemberId(null)
            }}
          />
        )}
        {store.activeModal === "addLoyaltyNumber" && (
          <AddLoyaltyNumberModal
            closeModal={() => {
              store.setActiveModal(null)
            }}
            cruiseLines={filteredCruiseLines}
            isLoading={isCruiseLinesLoading}
            profileId={store.profile?.id}
            setProfile={(profile) => store.setProfile(profile)}
            tripId={activeTrip.id}
            roomId={roomId}
            tripRoomGuestProfile={tripRoomGuestProfile}
          />
        )}
      </>
    )
  },
)
